import React, { useState } from 'react';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import MuiButton from '@mui/material/Button';
import ClearIcon from '@mui/icons-material/Clear';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import SearchIcon from '@mui/icons-material/Search';

type InputSize = 'small' | 'middle' | 'large';

export interface InputProps {
  value?: any;
  defaultValue?: any;
  onChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onPressEnter?: (e: React.KeyboardEvent) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onFocus?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onKeyDown?: React.KeyboardEventHandler;
  placeholder?: string;
  disabled?: boolean;
  size?: InputSize;
  allowClear?: boolean | { clearIcon?: React.ReactNode };
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  addonBefore?: React.ReactNode;
  addonAfter?: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
  type?: string;
  name?: string;
  id?: string;
  autoComplete?: string;
  maxLength?: number;
  readOnly?: boolean;
  bordered?: boolean;
  status?: 'error' | 'warning';
  onClick?: React.MouseEventHandler;
  [key: string]: any;
}

function muiSize(size?: InputSize): 'small' | 'medium' {
  return size === 'large' ? 'medium' : 'small'
}

const InputBase: React.FC<InputProps> = ({
  value,
  defaultValue,
  onChange,
  onPressEnter,
  onBlur,
  onFocus,
  onKeyDown,
  placeholder,
  disabled,
  size,
  allowClear,
  prefix,
  suffix,
  addonBefore,
  addonAfter,
  style,
  className,
  type = 'text',
  name,
  id,
  autoComplete,
  maxLength,
  readOnly,
  bordered,
  status,
  onClick,
  ...rest
}) => {
  void addonBefore;
  void addonAfter;
  void bordered;
  void status;
  const {
    inputRef,
    InputProps: restInputProps,
    inputProps: restInputPropsHtml,
    ...ignoredRest
  } = rest
  void ignoredRest;
  const [inner, setInner] = useState(defaultValue ?? '');
  const controlled = value !== undefined;
  const val = controlled ? value : inner;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (!controlled) setInner(e.target.value);
    onChange?.(e);
  };

  const allowClearEnabled = !!allowClear;
  const customClearIcon =
    typeof allowClear === 'object' && allowClear?.clearIcon != null ? allowClear.clearIcon : null;
  const clearBtn =
    allowClearEnabled && val ? (
      customClearIcon ?? (
        <IconButton size="small" onClick={() => handleChange({ target: { value: '' } } as any)} edge="end">
          <ClearIcon fontSize="small" />
        </IconButton>
      )
    ) : null;

  return (
    <TextField
      fullWidth
      size={muiSize(size)}
      className={className}
      type={type}
      value={val}
      placeholder={placeholder}
      disabled={disabled}
      inputRef={inputRef}
      inputProps={{ maxLength, readOnly, name, id, autoComplete, ...restInputPropsHtml }}
      onClick={onClick}
      onChange={handleChange}
      onBlur={onBlur}
      onFocus={onFocus}
      onKeyDown={(e) => {
        onKeyDown?.(e);
        if (e.key === 'Enter') onPressEnter?.(e);
      }}
      error={status === 'error'}
      sx={style}
      InputProps={{
        ...restInputProps,
        startAdornment: prefix ? (
          <InputAdornment position="start">{prefix}</InputAdornment>
        ) : (
          restInputProps?.startAdornment
        ),
        endAdornment: (
          <>
            {suffix && <InputAdornment position="end">{suffix}</InputAdornment>}
            {clearBtn}
            {restInputProps?.endAdornment}
          </>
        ),
      }}
    />
  );
};

const Password: React.FC<InputProps> = (props) => {
  const [show, setShow] = useState(false);
  return (
    <InputBase
      {...props}
      type={show ? 'text' : 'password'}
      suffix={
        <IconButton size="small" onClick={() => setShow((s) => !s)} edge="end">
          {show ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
        </IconButton>
      }
    />
  );
};

export interface SearchProps extends InputProps {
  onSearch?: (value: string) => void;
  enterButton?: boolean | React.ReactNode;
  loading?: boolean;
}

const Search: React.FC<SearchProps> = ({
  onSearch,
  enterButton = true,
  loading,
  value,
  onChange,
  ...rest
}) => {
  const val = value ?? '';
  const button =
    enterButton === false ? null : (
      <MuiButton
        variant="contained"
        size="small"
        disabled={loading}
        onClick={() => onSearch?.(typeof val === 'string' ? val : '')}
        sx={{ minWidth: enterButton === true ? 40 : undefined, textTransform: 'none' }}
      >
        {enterButton === true ? <SearchIcon /> : enterButton}
      </MuiButton>
    );

  return (
    <TextField
      fullWidth
      size={muiSize(rest.size)}
      value={value}
      onChange={onChange}
      placeholder={rest.placeholder}
      disabled={rest.disabled}
      className={rest.className}
      sx={rest.style}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onSearch?.((e.target as HTMLInputElement).value);
      }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            {button || (
              <IconButton size="small" onClick={() => onSearch?.(typeof val === 'string' ? val : '')}>
                <SearchIcon />
              </IconButton>
            )}
          </InputAdornment>
        ),
      }}
    />
  );
};

const TextArea: React.FC<InputProps & { rows?: number; autoSize?: boolean | { minRows?: number; maxRows?: number } }> = ({
  rows = 3,
  autoSize,
  ...props
}) => {
  const minRows = typeof autoSize === 'object' ? autoSize.minRows : autoSize ? 2 : rows;
  return (
    <TextField
      fullWidth
      multiline
      minRows={minRows}
      maxRows={typeof autoSize === 'object' ? autoSize.maxRows : undefined}
      size={muiSize(props.size)}
      value={props.value}
      defaultValue={props.defaultValue}
      placeholder={props.placeholder}
      disabled={props.disabled}
      className={props.className}
      sx={props.style}
      onChange={props.onChange}
    />
  );
};

export type InputComponent = React.FC<InputProps> & {
  Password: typeof Password;
  TextArea: typeof TextArea;
  Search: typeof Search;
};

export const Input = InputBase as InputComponent;
Input.Password = Password;
Input.TextArea = TextArea;
Input.Search = Search;

export default Input;
